/* eslint-disable react/no-danger */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import Cookies from 'universal-cookie';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackQueuedEvents, facebookPixelScript, facebookPixelNoscript } from '../../actions/facebook_analytics';
import { initFirebaseMessaging } from '../../actions/firebase_messaging';
import { hotJarInitialize, hotJarIdentifyUser } from '../../actions/hotjar';
import { sectionIds } from '../home_page/prop_types';
import { gogleLoginScript } from '../registration/inner_components/google_btn';
import { getAppTheme, fromMobileApp, compareObjects } from '../../../common/config/utils';
import { appThemes } from '../../../common/config/const';
import { trackQueuedOptimoveEvents, trackFirstVisitEventGtm } from '../../actions/google_tag_mahager_events';
import env from '../../config/variables';
import { isPrerenderMode } from '../../config/util';
import { getSpriteUrl, loadSVGSprite } from '../../../common/sprites/loadSprite';
import { firstVisitEventsApi, getClientSettingsApi } from '../../config/api';
import { log, LOG_LEVELS } from '../../../common/config/app_logger';

const { shared } = window;

const cookies = new Cookies();

const cookieValue = (valueName) => {
  const valueString = document.cookie.split('; ').find(row => row.startsWith(`${ valueName }=`));
  const value = valueString ? valueString.split('=')[1] : null;
  return value;
};

const { PAYMENT_PROCESSING_INTERVAL_SEC } = env;

const retryRequest = ({
  apiFunction, maxRetries, delay = 2000, attempt = 1
}) => apiFunction().catch(error => {
  if (maxRetries && attempt >= maxRetries) throw error;
  return new Promise(resolve => setTimeout(resolve, delay))
    .then(() => retryRequest({
      apiFunction, maxRetries, delay, attempt: attempt + 1
    }));
});

function AnalyticsAndCommonLogicComponent({
  userId,
  configureMP,
  trackPeoplePropertiesAction,
  getUser,
  requestPermission,
  uploadLogs,
  logKey,
  logUrl,
  parseAndUploadLogs,
  identifyOrAliasUserOnMP,
  user,
  loadHomePage,
  userPaymentProcessing,
  getMyAccountCredits,
  appLoading,
  getAnonymousUser,
  userFirstActivePromotion,
  initFirebaseDatabase,
  signOutFromFBDatabase,
  setDynamicConfig,
  processQueuedTrackingEvents,
  dynamicConfig
}) {
  const setAppTheme = () => {
    const appTheme = getAppTheme();
    if (appTheme === appThemes.dark) {
      document.body.classList.add(`${ shared.darkTheme }`);
    }
  };
  const initializeData = async () => {
    try {
      if (window.currentUserId) {
        const [clientSettingsRes] = await Promise.all([
          retryRequest({ apiFunction: getClientSettingsApi, delay: 2000 }),
          getUser(window.currentUserId)
        ]);
        setDynamicConfig(clientSettingsRes, true);
      } else {
        const [firstVisitRes] = await Promise.all([
          retryRequest({ apiFunction: firstVisitEventsApi, delay: 2000 }),
          getAnonymousUser()
        ]);
        setDynamicConfig(firstVisitRes, true);
      }
    } catch (error) {
      log(LOG_LEVELS.ERROR, 'initializeData', 'Error initializing data with retries:', error);
      try {
        const apiFunction = window.currentUserId ? getClientSettingsApi : firstVisitEventsApi;
        const res = await apiFunction();
        setDynamicConfig(res, true);
      } catch (fallbackError) {
        log(LOG_LEVELS.ERROR, 'initializeData', 'Fallback failed:', fallbackError);
      }
    }
  };

  useEffect(() => {
    loadSVGSprite(getSpriteUrl(), 'svg-sprite-container');
  }, []);

  useEffect(() => {
    setAppTheme();
    setTimeout(() => appLoading(), 1000);
    if (!cookieValue('visited')) {
      trackFirstVisitEventGtm();
      cookies.set('visited', true, {
        path: '/'
      });
    }
  }, []);

  useEffect(() => {
    initializeData();
    initFirebaseMessaging();
    configureMP();
    hotJarInitialize();
    if (!fromMobileApp && !isPrerenderMode()) {
      loadHomePage(false, { section_ids: sectionIds.salesBanner });
    }
  }, []);

  useEffect(() => {
    if (dynamicConfig?.enableAnalytics) {
      processQueuedTrackingEvents();
    }
  }, [dynamicConfig]);

  useEffect(() => {
    trackQueuedEvents();
  }, [window.facebookPixelConfigured, window.FB, userId]);

  useEffect(() => {
    if (window.optimoveSDK && window.optimoveSDK !== undefined) {
      trackQueuedOptimoveEvents();
    }
  }, [window.optimoveSDK]);

  useEffect(() => {
    if (!userId) {
      signOutFromFBDatabase();
      return;
    }
    identifyOrAliasUserOnMP();
    trackPeoplePropertiesAction();
    initFirebaseDatabase(user);
  }, [userId]);

  useEffect(() => {
    if (!userId || !dynamicConfig) return;
    hotJarIdentifyUser({ user });
  }, [user, dynamicConfig]);

  useEffect(() => {
    if (!userId) return;
    requestPermission();
  }, [userId]);

  useEffect(() => {
    if (uploadLogs) {
      parseAndUploadLogs(logUrl, logKey);
    }
  }, [uploadLogs]);

  useEffect(() => {
    if (userPaymentProcessing) {
      const interval = setInterval(() => {
        getMyAccountCredits();
      }, PAYMENT_PROCESSING_INTERVAL_SEC * 1000);
      return () => {
        if (interval) clearInterval(interval);
      };
    }
    return () => { };
  }, [userPaymentProcessing]);

  const history = useNavigate();
  const location = useLocation();

  const [userFirstActivePromotionState, setUserFirstActivePromotionState] = useState(userFirstActivePromotion);

  const shouldUpdatePageWithAdvisors = useMemo(() => !compareObjects({ ...userFirstActivePromotion, promoTimer: undefined }, { ...userFirstActivePromotionState, promoTimer: undefined })
    && (userFirstActivePromotion.refreshStrategy === 'all_advisors'), [userFirstActivePromotion, userFirstActivePromotionState]);

  useEffect(() => {
    if (shouldUpdatePageWithAdvisors) {
      setUserFirstActivePromotionState(prevState => {
        if (compareObjects(userFirstActivePromotion, prevState)) return prevState;
        return {
          ...prevState,
          ...userFirstActivePromotion
        };
      });
      history(`${ location.pathname }${ location.search }`, {
        state: {
          ...location.state, needReloadHomePage: true, needReloadSearchResults: true, needReloadAdvisorPage: true
        },
        replace: true
      });
    }
  }, [shouldUpdatePageWithAdvisors]);

  const addFacebookPixelNoscript = () => {
    if (!document.getElementById('facebook_pixel_noscript')) {
      document.body.appendChild(facebookPixelNoscript());
    }
  };

  const addScriptsToBody = () => {
    addFacebookPixelNoscript();
  };

  const addScripts = () => (
    <>
      <Helmet>
        {facebookPixelScript()}
        {gogleLoginScript()}
      </Helmet>
      {addScriptsToBody()}
      <div
        id="svg-sprite-container"
        style={ { position: 'absolute', top: '-1500px' } }
        suppressHydrationWarning
        dangerouslySetInnerHTML={ { __html: '<svg />' } }
      />
    </>
  );

  return addScripts();
}

AnalyticsAndCommonLogicComponent.propTypes = {
  configureMP: PropTypes.func.isRequired,
  trackPeoplePropertiesAction: PropTypes.func.isRequired,
  getUser: PropTypes.func.isRequired,
  requestPermission: PropTypes.func.isRequired,
  userId: PropTypes.number,
  uploadLogs: PropTypes.bool,
  logUrl: PropTypes.string,
  logKey: PropTypes.string,
  parseAndUploadLogs: PropTypes.func.isRequired,
  identifyOrAliasUserOnMP: PropTypes.func.isRequired,
  user: PropTypes.object,
  loadHomePage: PropTypes.func.isRequired,
  userPaymentProcessing: PropTypes.bool,
  getMyAccountCredits: PropTypes.func.isRequired,
  appLoading: PropTypes.func.isRequired,
  getAnonymousUser: PropTypes.func.isRequired,
  userFirstActivePromotion: PropTypes.object,
  initFirebaseDatabase: PropTypes.func.isRequired,
  signOutFromFBDatabase: PropTypes.func.isRequired,
  setDynamicConfig: PropTypes.func.isRequired,
  processQueuedTrackingEvents: PropTypes.func.isRequired,
  dynamicConfig: PropTypes.object
};

AnalyticsAndCommonLogicComponent.defaultProps = {
  userId: null,
  uploadLogs: false,
  logUrl: null,
  logKey: null,
  user: null,
  userPaymentProcessing: null,
  userFirstActivePromotion: null,
  dynamicConfig: null
};

export default AnalyticsAndCommonLogicComponent;
